/**
 * Lottie-Animationsbibliothek fuer den Creator Studio
 * Animierte Elemente fuer Social Media Content
 *
 * Beinhaltet einfache Inline-Animationen und Placeholder-Definitionen
 * fuer komplexere Animationen, die spaeter geladen werden koennen.
 */

import type { AnimationElement } from './elementTypes';

// ===== HELPER: Einfache Lottie JSON Strukturen =====

/**
 * Erzeugt ein minimales Lottie JSON fuer eine Kreis-Puls-Animation
 */
function createPulseAnimation(color: string = '#6366F1'): object {
  return {
    v: '5.7.1',
    fr: 30,
    ip: 0,
    op: 60,
    w: 200,
    h: 200,
    nm: 'Pulse',
    ddd: 0,
    assets: [],
    layers: [
      {
        ddd: 0,
        ind: 1,
        ty: 4,
        nm: 'Circle',
        sr: 1,
        ks: {
          o: {
            a: 1,
            k: [
              { t: 0, s: [100], e: [30] },
              { t: 30, s: [30], e: [100] },
              { t: 60, s: [100] }
            ]
          },
          r: { a: 0, k: 0 },
          p: { a: 0, k: [100, 100, 0] },
          a: { a: 0, k: [0, 0, 0] },
          s: {
            a: 1,
            k: [
              { t: 0, s: [100, 100, 100], e: [120, 120, 100] },
              { t: 30, s: [120, 120, 100], e: [100, 100, 100] },
              { t: 60, s: [100, 100, 100] }
            ]
          }
        },
        ao: 0,
        shapes: [
          {
            ty: 'el',
            s: { a: 0, k: [80, 80] },
            p: { a: 0, k: [0, 0] },
            nm: 'Ellipse'
          },
          {
            ty: 'fl',
            c: { a: 0, k: hexToRgba(color) },
            o: { a: 0, k: 100 },
            r: 1,
            nm: 'Fill'
          }
        ],
        ip: 0,
        op: 60,
        st: 0
      }
    ]
  };
}

/**
 * Erzeugt ein minimales Lottie JSON fuer einen einfachen Spinner
 */
function createSpinnerAnimation(color: string = '#6366F1'): object {
  return {
    v: '5.7.1',
    fr: 30,
    ip: 0,
    op: 60,
    w: 200,
    h: 200,
    nm: 'Spinner',
    ddd: 0,
    assets: [],
    layers: [
      {
        ddd: 0,
        ind: 1,
        ty: 4,
        nm: 'Spinner',
        sr: 1,
        ks: {
          o: { a: 0, k: 100 },
          r: {
            a: 1,
            k: [
              { t: 0, s: [0], e: [360] },
              { t: 60, s: [360] }
            ]
          },
          p: { a: 0, k: [100, 100, 0] },
          a: { a: 0, k: [0, 0, 0] },
          s: { a: 0, k: [100, 100, 100] }
        },
        ao: 0,
        shapes: [
          {
            ty: 'el',
            s: { a: 0, k: [80, 80] },
            p: { a: 0, k: [0, 0] },
            nm: 'Ellipse'
          },
          {
            ty: 'st',
            c: { a: 0, k: hexToRgba(color) },
            o: { a: 0, k: 100 },
            w: { a: 0, k: 8 },
            lc: 2,
            lj: 2,
            nm: 'Stroke'
          },
          {
            ty: 'tm',
            s: { a: 0, k: 0 },
            e: { a: 0, k: 75 },
            o: { a: 0, k: 0 },
            m: 1,
            nm: 'Trim'
          }
        ],
        ip: 0,
        op: 60,
        st: 0
      }
    ]
  };
}

/**
 * Erzeugt ein minimales Lottie JSON fuer einen Checkmark
 */
function createCheckmarkAnimation(color: string = '#10B981'): object {
  return {
    v: '5.7.1',
    fr: 30,
    ip: 0,
    op: 30,
    w: 200,
    h: 200,
    nm: 'Checkmark',
    ddd: 0,
    assets: [],
    layers: [
      {
        ddd: 0,
        ind: 1,
        ty: 4,
        nm: 'Check',
        sr: 1,
        ks: {
          o: { a: 0, k: 100 },
          r: { a: 0, k: 0 },
          p: { a: 0, k: [100, 100, 0] },
          a: { a: 0, k: [0, 0, 0] },
          s: {
            a: 1,
            k: [
              { t: 0, s: [0, 0, 100], e: [110, 110, 100] },
              { t: 15, s: [110, 110, 100], e: [100, 100, 100] },
              { t: 20, s: [100, 100, 100] }
            ]
          }
        },
        ao: 0,
        shapes: [
          {
            ty: 'sh',
            ks: {
              a: 0,
              k: {
                i: [[0, 0], [0, 0], [0, 0]],
                o: [[0, 0], [0, 0], [0, 0]],
                v: [[-30, 0], [-10, 20], [30, -20]],
                c: false
              }
            },
            nm: 'Path'
          },
          {
            ty: 'st',
            c: { a: 0, k: hexToRgba(color) },
            o: { a: 0, k: 100 },
            w: { a: 0, k: 12 },
            lc: 2,
            lj: 2,
            nm: 'Stroke'
          },
          {
            ty: 'tm',
            s: { a: 0, k: 0 },
            e: {
              a: 1,
              k: [
                { t: 0, s: [0], e: [100] },
                { t: 20, s: [100] }
              ]
            },
            o: { a: 0, k: 0 },
            m: 1,
            nm: 'Trim'
          }
        ],
        ip: 0,
        op: 30,
        st: 0
      },
      {
        ddd: 0,
        ind: 2,
        ty: 4,
        nm: 'Circle',
        sr: 1,
        ks: {
          o: { a: 0, k: 100 },
          r: { a: 0, k: 0 },
          p: { a: 0, k: [100, 100, 0] },
          a: { a: 0, k: [0, 0, 0] },
          s: {
            a: 1,
            k: [
              { t: 0, s: [0, 0, 100], e: [100, 100, 100] },
              { t: 15, s: [100, 100, 100] }
            ]
          }
        },
        ao: 0,
        shapes: [
          {
            ty: 'el',
            s: { a: 0, k: [100, 100] },
            p: { a: 0, k: [0, 0] },
            nm: 'Ellipse'
          },
          {
            ty: 'fl',
            c: { a: 0, k: [...hexToRgba(color).slice(0, 3), 0.2] },
            o: { a: 0, k: 20 },
            r: 1,
            nm: 'Fill'
          }
        ],
        ip: 0,
        op: 30,
        st: 0
      }
    ]
  };
}

/**
 * Erzeugt ein minimales Lottie JSON fuer ein pulsierendes Herz
 */
function createHeartPulseAnimation(): object {
  return {
    v: '5.7.1',
    fr: 30,
    ip: 0,
    op: 45,
    w: 200,
    h: 200,
    nm: 'Heart Pulse',
    ddd: 0,
    assets: [],
    layers: [
      {
        ddd: 0,
        ind: 1,
        ty: 4,
        nm: 'Heart',
        sr: 1,
        ks: {
          o: { a: 0, k: 100 },
          r: { a: 0, k: 0 },
          p: { a: 0, k: [100, 105, 0] },
          a: { a: 0, k: [0, 0, 0] },
          s: {
            a: 1,
            k: [
              { t: 0, s: [100, 100, 100], e: [115, 115, 100] },
              { t: 10, s: [115, 115, 100], e: [95, 95, 100] },
              { t: 20, s: [95, 95, 100], e: [105, 105, 100] },
              { t: 30, s: [105, 105, 100], e: [100, 100, 100] },
              { t: 45, s: [100, 100, 100] }
            ]
          }
        },
        ao: 0,
        shapes: [
          {
            ty: 'sh',
            ks: {
              a: 0,
              k: {
                i: [[0, 0], [-15, -20], [0, -25], [20, 0], [5, 10], [5, -10], [20, 0], [0, 25], [-15, 20]],
                o: [[15, 20], [0, 25], [-20, 0], [-5, -10], [-5, 10], [-20, 0], [0, -25], [15, -20], [0, 0]],
                v: [[0, 35], [40, 0], [40, -25], [0, -40], [0, -25], [0, -40], [-40, -25], [-40, 0], [0, 35]],
                c: true
              }
            },
            nm: 'Heart Path'
          },
          {
            ty: 'fl',
            c: { a: 0, k: [0.937, 0.267, 0.267, 1] },
            o: { a: 0, k: 100 },
            r: 1,
            nm: 'Fill'
          }
        ],
        ip: 0,
        op: 45,
        st: 0
      }
    ]
  };
}

/**
 * Erzeugt Lottie JSON fuer Ladepunkte
 */
function createDotsLoadingAnimation(color: string = '#6366F1'): object {
  return {
    v: '5.7.1',
    fr: 30,
    ip: 0,
    op: 60,
    w: 200,
    h: 100,
    nm: 'Loading Dots',
    ddd: 0,
    assets: [],
    layers: [
      {
        ddd: 0,
        ind: 1,
        ty: 4,
        nm: 'Dot 1',
        sr: 1,
        ks: {
          o: {
            a: 1,
            k: [
              { t: 0, s: [100], e: [30] },
              { t: 15, s: [30], e: [100] },
              { t: 30, s: [100] }
            ]
          },
          r: { a: 0, k: 0 },
          p: { a: 0, k: [60, 50, 0] },
          a: { a: 0, k: [0, 0, 0] },
          s: {
            a: 1,
            k: [
              { t: 0, s: [100, 100], e: [70, 70] },
              { t: 15, s: [70, 70], e: [100, 100] },
              { t: 30, s: [100, 100] }
            ]
          }
        },
        ao: 0,
        shapes: [
          { ty: 'el', s: { a: 0, k: [20, 20] }, p: { a: 0, k: [0, 0] }, nm: 'Ellipse' },
          { ty: 'fl', c: { a: 0, k: hexToRgba(color) }, o: { a: 0, k: 100 }, r: 1, nm: 'Fill' }
        ],
        ip: 0,
        op: 60,
        st: 0
      },
      {
        ddd: 0,
        ind: 2,
        ty: 4,
        nm: 'Dot 2',
        sr: 1,
        ks: {
          o: {
            a: 1,
            k: [
              { t: 10, s: [100], e: [30] },
              { t: 25, s: [30], e: [100] },
              { t: 40, s: [100] }
            ]
          },
          r: { a: 0, k: 0 },
          p: { a: 0, k: [100, 50, 0] },
          a: { a: 0, k: [0, 0, 0] },
          s: {
            a: 1,
            k: [
              { t: 10, s: [100, 100], e: [70, 70] },
              { t: 25, s: [70, 70], e: [100, 100] },
              { t: 40, s: [100, 100] }
            ]
          }
        },
        ao: 0,
        shapes: [
          { ty: 'el', s: { a: 0, k: [20, 20] }, p: { a: 0, k: [0, 0] }, nm: 'Ellipse' },
          { ty: 'fl', c: { a: 0, k: hexToRgba(color) }, o: { a: 0, k: 100 }, r: 1, nm: 'Fill' }
        ],
        ip: 0,
        op: 60,
        st: 0
      },
      {
        ddd: 0,
        ind: 3,
        ty: 4,
        nm: 'Dot 3',
        sr: 1,
        ks: {
          o: {
            a: 1,
            k: [
              { t: 20, s: [100], e: [30] },
              { t: 35, s: [30], e: [100] },
              { t: 50, s: [100] }
            ]
          },
          r: { a: 0, k: 0 },
          p: { a: 0, k: [140, 50, 0] },
          a: { a: 0, k: [0, 0, 0] },
          s: {
            a: 1,
            k: [
              { t: 20, s: [100, 100], e: [70, 70] },
              { t: 35, s: [70, 70], e: [100, 100] },
              { t: 50, s: [100, 100] }
            ]
          }
        },
        ao: 0,
        shapes: [
          { ty: 'el', s: { a: 0, k: [20, 20] }, p: { a: 0, k: [0, 0] }, nm: 'Ellipse' },
          { ty: 'fl', c: { a: 0, k: hexToRgba(color) }, o: { a: 0, k: 100 }, r: 1, nm: 'Fill' }
        ],
        ip: 0,
        op: 60,
        st: 0
      }
    ]
  };
}

/**
 * Erzeugt Lottie JSON fuer Sparkles/Funken
 */
function createSparklesAnimation(): object {
  return {
    v: '5.7.1',
    fr: 30,
    ip: 0,
    op: 45,
    w: 200,
    h: 200,
    nm: 'Sparkles',
    ddd: 0,
    assets: [],
    layers: [
      {
        ddd: 0,
        ind: 1,
        ty: 4,
        nm: 'Sparkle 1',
        sr: 1,
        ks: {
          o: {
            a: 1,
            k: [
              { t: 0, s: [0], e: [100] },
              { t: 10, s: [100], e: [100] },
              { t: 35, s: [100], e: [0] },
              { t: 45, s: [0] }
            ]
          },
          r: {
            a: 1,
            k: [
              { t: 0, s: [0], e: [180] },
              { t: 45, s: [180] }
            ]
          },
          p: { a: 0, k: [70, 60, 0] },
          a: { a: 0, k: [0, 0, 0] },
          s: {
            a: 1,
            k: [
              { t: 0, s: [0, 0], e: [100, 100] },
              { t: 15, s: [100, 100], e: [80, 80] },
              { t: 45, s: [80, 80] }
            ]
          }
        },
        ao: 0,
        shapes: [
          {
            ty: 'sr',
            sy: 1,
            d: 1,
            pt: { a: 0, k: 4 },
            p: { a: 0, k: [0, 0] },
            r: { a: 0, k: 0 },
            ir: { a: 0, k: 5 },
            is: { a: 0, k: 0 },
            or: { a: 0, k: 15 },
            os: { a: 0, k: 0 },
            nm: 'Star'
          },
          { ty: 'fl', c: { a: 0, k: [0.988, 0.749, 0.286, 1] }, o: { a: 0, k: 100 }, r: 1, nm: 'Fill' }
        ],
        ip: 0,
        op: 45,
        st: 0
      },
      {
        ddd: 0,
        ind: 2,
        ty: 4,
        nm: 'Sparkle 2',
        sr: 1,
        ks: {
          o: {
            a: 1,
            k: [
              { t: 5, s: [0], e: [100] },
              { t: 15, s: [100], e: [100] },
              { t: 40, s: [100], e: [0] },
              { t: 45, s: [0] }
            ]
          },
          r: {
            a: 1,
            k: [
              { t: 5, s: [0], e: [-180] },
              { t: 45, s: [-180] }
            ]
          },
          p: { a: 0, k: [130, 80, 0] },
          a: { a: 0, k: [0, 0, 0] },
          s: {
            a: 1,
            k: [
              { t: 5, s: [0, 0], e: [80, 80] },
              { t: 20, s: [80, 80], e: [60, 60] },
              { t: 45, s: [60, 60] }
            ]
          }
        },
        ao: 0,
        shapes: [
          {
            ty: 'sr',
            sy: 1,
            d: 1,
            pt: { a: 0, k: 4 },
            p: { a: 0, k: [0, 0] },
            r: { a: 0, k: 0 },
            ir: { a: 0, k: 4 },
            is: { a: 0, k: 0 },
            or: { a: 0, k: 12 },
            os: { a: 0, k: 0 },
            nm: 'Star'
          },
          { ty: 'fl', c: { a: 0, k: [0.988, 0.749, 0.286, 1] }, o: { a: 0, k: 100 }, r: 1, nm: 'Fill' }
        ],
        ip: 0,
        op: 45,
        st: 0
      },
      {
        ddd: 0,
        ind: 3,
        ty: 4,
        nm: 'Sparkle 3',
        sr: 1,
        ks: {
          o: {
            a: 1,
            k: [
              { t: 10, s: [0], e: [100] },
              { t: 20, s: [100], e: [100] },
              { t: 40, s: [100], e: [0] },
              { t: 45, s: [0] }
            ]
          },
          r: {
            a: 1,
            k: [
              { t: 10, s: [0], e: [180] },
              { t: 45, s: [180] }
            ]
          },
          p: { a: 0, k: [100, 130, 0] },
          a: { a: 0, k: [0, 0, 0] },
          s: {
            a: 1,
            k: [
              { t: 10, s: [0, 0], e: [120, 120] },
              { t: 25, s: [120, 120], e: [100, 100] },
              { t: 45, s: [100, 100] }
            ]
          }
        },
        ao: 0,
        shapes: [
          {
            ty: 'sr',
            sy: 1,
            d: 1,
            pt: { a: 0, k: 4 },
            p: { a: 0, k: [0, 0] },
            r: { a: 0, k: 0 },
            ir: { a: 0, k: 6 },
            is: { a: 0, k: 0 },
            or: { a: 0, k: 18 },
            os: { a: 0, k: 0 },
            nm: 'Star'
          },
          { ty: 'fl', c: { a: 0, k: [0.988, 0.749, 0.286, 1] }, o: { a: 0, k: 100 }, r: 1, nm: 'Fill' }
        ],
        ip: 0,
        op: 45,
        st: 0
      }
    ]
  };
}

/**
 * Hilfsfunktion: Konvertiert Hex-Farbe zu RGBA Array [r, g, b, a] (0-1)
 */
function hexToRgba(hex: string): [number, number, number, number] {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (result) {
    return [
      parseInt(result[1], 16) / 255,
      parseInt(result[2], 16) / 255,
      parseInt(result[3], 16) / 255,
      1
    ];
  }
  return [0.39, 0.4, 0.95, 1]; // Default: #6366F1
}

// ===== SOCIAL ICONS ANIMATIONEN =====
const socialAnimations: AnimationElement[] = [
  {
    id: 'anim-heart-pulse',
    type: 'animation',
    name: 'Herz Puls',
    tags: ['herz', 'like', 'puls', 'liebe', 'social'],
    category: 'social',
    animationData: createHeartPulseAnimation(),
    duration: 1500,
    loop: true,
    autoplay: true,
  },
  {
    id: 'anim-share',
    type: 'animation',
    name: 'Teilen',
    tags: ['teilen', 'share', 'senden', 'social'],
    category: 'social',
    animationData: {
      // Placeholder fuer komplexere Animation
      v: '5.7.1',
      fr: 30,
      ip: 0,
      op: 45,
      w: 200,
      h: 200,
      nm: 'Share',
      layers: []
    },
    duration: 1500,
    loop: false,
    autoplay: true,
  },
  {
    id: 'anim-comment-bubble',
    type: 'animation',
    name: 'Kommentar Blase',
    tags: ['kommentar', 'chat', 'blase', 'nachricht', 'social'],
    category: 'social',
    animationData: {
      v: '5.7.1',
      fr: 30,
      ip: 0,
      op: 60,
      w: 200,
      h: 200,
      nm: 'Comment Bubble',
      layers: []
    },
    duration: 2000,
    loop: true,
    autoplay: true,
  },
  {
    id: 'anim-notification',
    type: 'animation',
    name: 'Benachrichtigung',
    tags: ['notification', 'glocke', 'alert', 'social'],
    category: 'social',
    animationData: {
      v: '5.7.1',
      fr: 30,
      ip: 0,
      op: 45,
      w: 200,
      h: 200,
      nm: 'Notification',
      layers: []
    },
    duration: 1500,
    loop: false,
    autoplay: true,
  },
  {
    id: 'anim-follow',
    type: 'animation',
    name: 'Folgen',
    tags: ['follow', 'folgen', 'abonnieren', 'social'],
    category: 'social',
    animationData: {
      v: '5.7.1',
      fr: 30,
      ip: 0,
      op: 30,
      w: 200,
      h: 200,
      nm: 'Follow',
      layers: []
    },
    duration: 1000,
    loop: false,
    autoplay: true,
  },
];

// ===== LOADING ANIMATIONEN =====
const loadingAnimations: AnimationElement[] = [
  {
    id: 'anim-spinner',
    type: 'animation',
    name: 'Spinner',
    tags: ['spinner', 'laden', 'loading', 'warten'],
    category: 'loading',
    animationData: createSpinnerAnimation('#6366F1'),
    duration: 2000,
    loop: true,
    autoplay: true,
  },
  {
    id: 'anim-dots',
    type: 'animation',
    name: 'Ladepunkte',
    tags: ['punkte', 'dots', 'laden', 'loading'],
    category: 'loading',
    animationData: createDotsLoadingAnimation('#6366F1'),
    duration: 2000,
    loop: true,
    autoplay: true,
  },
  {
    id: 'anim-progress',
    type: 'animation',
    name: 'Fortschritt',
    tags: ['fortschritt', 'progress', 'laden', 'bar'],
    category: 'loading',
    animationData: {
      v: '5.7.1',
      fr: 30,
      ip: 0,
      op: 90,
      w: 300,
      h: 50,
      nm: 'Progress',
      layers: []
    },
    duration: 3000,
    loop: false,
    autoplay: true,
  },
  {
    id: 'anim-pulse-circle',
    type: 'animation',
    name: 'Puls Kreis',
    tags: ['puls', 'kreis', 'laden', 'pulse'],
    category: 'loading',
    animationData: createPulseAnimation('#6366F1'),
    duration: 2000,
    loop: true,
    autoplay: true,
  },
  {
    id: 'anim-hourglass',
    type: 'animation',
    name: 'Sanduhr',
    tags: ['sanduhr', 'zeit', 'warten', 'loading'],
    category: 'loading',
    animationData: {
      v: '5.7.1',
      fr: 30,
      ip: 0,
      op: 90,
      w: 200,
      h: 200,
      nm: 'Hourglass',
      layers: []
    },
    duration: 3000,
    loop: true,
    autoplay: true,
  },
];

// ===== SUCCESS ANIMATIONEN =====
const successAnimations: AnimationElement[] = [
  {
    id: 'anim-checkmark',
    type: 'animation',
    name: 'Haekchen',
    tags: ['check', 'haekchen', 'erfolg', 'fertig'],
    category: 'success',
    animationData: createCheckmarkAnimation('#10B981'),
    duration: 1000,
    loop: false,
    autoplay: true,
  },
  {
    id: 'anim-confetti',
    type: 'animation',
    name: 'Konfetti',
    tags: ['konfetti', 'feier', 'party', 'erfolg'],
    category: 'success',
    animationData: {
      v: '5.7.1',
      fr: 30,
      ip: 0,
      op: 90,
      w: 300,
      h: 300,
      nm: 'Confetti',
      layers: []
    },
    duration: 3000,
    loop: false,
    autoplay: true,
  },
  {
    id: 'anim-thumbs-up',
    type: 'animation',
    name: 'Daumen hoch',
    tags: ['daumen', 'like', 'gut', 'erfolg'],
    category: 'success',
    animationData: {
      v: '5.7.1',
      fr: 30,
      ip: 0,
      op: 45,
      w: 200,
      h: 200,
      nm: 'Thumbs Up',
      layers: []
    },
    duration: 1500,
    loop: false,
    autoplay: true,
  },
  {
    id: 'anim-star-burst',
    type: 'animation',
    name: 'Stern Explosion',
    tags: ['stern', 'explosion', 'erfolg', 'bewertung'],
    category: 'success',
    animationData: {
      v: '5.7.1',
      fr: 30,
      ip: 0,
      op: 60,
      w: 200,
      h: 200,
      nm: 'Star Burst',
      layers: []
    },
    duration: 2000,
    loop: false,
    autoplay: true,
  },
  {
    id: 'anim-trophy',
    type: 'animation',
    name: 'Pokal',
    tags: ['pokal', 'gewinner', 'erfolg', 'preis'],
    category: 'success',
    animationData: {
      v: '5.7.1',
      fr: 30,
      ip: 0,
      op: 60,
      w: 200,
      h: 200,
      nm: 'Trophy',
      layers: []
    },
    duration: 2000,
    loop: false,
    autoplay: true,
  },
];

// ===== TRANSITION ANIMATIONEN =====
const transitionAnimations: AnimationElement[] = [
  {
    id: 'anim-fade',
    type: 'animation',
    name: 'Einblenden',
    tags: ['fade', 'einblenden', 'transition', 'uebergang'],
    category: 'transitions',
    animationData: {
      v: '5.7.1',
      fr: 30,
      ip: 0,
      op: 30,
      w: 200,
      h: 200,
      nm: 'Fade',
      layers: []
    },
    duration: 1000,
    loop: false,
    autoplay: true,
  },
  {
    id: 'anim-slide-in',
    type: 'animation',
    name: 'Hineingleiten',
    tags: ['slide', 'gleiten', 'transition', 'uebergang'],
    category: 'transitions',
    animationData: {
      v: '5.7.1',
      fr: 30,
      ip: 0,
      op: 30,
      w: 200,
      h: 200,
      nm: 'Slide In',
      layers: []
    },
    duration: 1000,
    loop: false,
    autoplay: true,
  },
  {
    id: 'anim-bounce-in',
    type: 'animation',
    name: 'Huepfen',
    tags: ['bounce', 'huepfen', 'transition', 'uebergang'],
    category: 'transitions',
    animationData: {
      v: '5.7.1',
      fr: 30,
      ip: 0,
      op: 45,
      w: 200,
      h: 200,
      nm: 'Bounce In',
      layers: []
    },
    duration: 1500,
    loop: false,
    autoplay: true,
  },
  {
    id: 'anim-scale-up',
    type: 'animation',
    name: 'Vergroessern',
    tags: ['scale', 'groesse', 'transition', 'zoom'],
    category: 'transitions',
    animationData: {
      v: '5.7.1',
      fr: 30,
      ip: 0,
      op: 30,
      w: 200,
      h: 200,
      nm: 'Scale Up',
      layers: []
    },
    duration: 1000,
    loop: false,
    autoplay: true,
  },
  {
    id: 'anim-rotate-in',
    type: 'animation',
    name: 'Drehen',
    tags: ['rotate', 'drehen', 'transition', 'spin'],
    category: 'transitions',
    animationData: {
      v: '5.7.1',
      fr: 30,
      ip: 0,
      op: 30,
      w: 200,
      h: 200,
      nm: 'Rotate In',
      layers: []
    },
    duration: 1000,
    loop: false,
    autoplay: true,
  },
];

// ===== DECORATIVE ANIMATIONEN =====
const decorativeAnimations: AnimationElement[] = [
  {
    id: 'anim-sparkles',
    type: 'animation',
    name: 'Funkeln',
    tags: ['sparkle', 'funkeln', 'glitzer', 'deko'],
    category: 'decorative',
    animationData: createSparklesAnimation(),
    duration: 1500,
    loop: true,
    autoplay: true,
  },
  {
    id: 'anim-stars',
    type: 'animation',
    name: 'Sterne',
    tags: ['sterne', 'stars', 'himmel', 'deko'],
    category: 'decorative',
    animationData: {
      v: '5.7.1',
      fr: 30,
      ip: 0,
      op: 90,
      w: 300,
      h: 200,
      nm: 'Stars',
      layers: []
    },
    duration: 3000,
    loop: true,
    autoplay: true,
  },
  {
    id: 'anim-waves',
    type: 'animation',
    name: 'Wellen',
    tags: ['wellen', 'waves', 'wasser', 'deko'],
    category: 'decorative',
    animationData: {
      v: '5.7.1',
      fr: 30,
      ip: 0,
      op: 120,
      w: 400,
      h: 100,
      nm: 'Waves',
      layers: []
    },
    duration: 4000,
    loop: true,
    autoplay: true,
  },
  {
    id: 'anim-bubbles',
    type: 'animation',
    name: 'Blasen',
    tags: ['blasen', 'bubbles', 'seifenblasen', 'deko'],
    category: 'decorative',
    animationData: {
      v: '5.7.1',
      fr: 30,
      ip: 0,
      op: 120,
      w: 200,
      h: 300,
      nm: 'Bubbles',
      layers: []
    },
    duration: 4000,
    loop: true,
    autoplay: true,
  },
  {
    id: 'anim-particles',
    type: 'animation',
    name: 'Partikel',
    tags: ['partikel', 'particles', 'staub', 'deko'],
    category: 'decorative',
    animationData: {
      v: '5.7.1',
      fr: 30,
      ip: 0,
      op: 90,
      w: 300,
      h: 300,
      nm: 'Particles',
      layers: []
    },
    duration: 3000,
    loop: true,
    autoplay: true,
  },
  {
    id: 'anim-gradient-morph',
    type: 'animation',
    name: 'Gradient Morph',
    tags: ['gradient', 'verlauf', 'morph', 'deko'],
    category: 'decorative',
    animationData: {
      v: '5.7.1',
      fr: 30,
      ip: 0,
      op: 150,
      w: 200,
      h: 200,
      nm: 'Gradient Morph',
      layers: []
    },
    duration: 5000,
    loop: true,
    autoplay: true,
  },
  {
    id: 'anim-floating',
    type: 'animation',
    name: 'Schwebend',
    tags: ['schwebend', 'floating', 'smooth', 'deko'],
    category: 'decorative',
    animationData: {
      v: '5.7.1',
      fr: 30,
      ip: 0,
      op: 120,
      w: 200,
      h: 200,
      nm: 'Floating',
      layers: []
    },
    duration: 4000,
    loop: true,
    autoplay: true,
  },
  {
    id: 'anim-glow',
    type: 'animation',
    name: 'Gluehen',
    tags: ['glow', 'gluehen', 'leuchten', 'deko'],
    category: 'decorative',
    animationData: createPulseAnimation('#FBBF24'),
    duration: 2000,
    loop: true,
    autoplay: true,
  },
];

// ===== ERROR/ALERT ANIMATIONEN =====
const alertAnimations: AnimationElement[] = [
  {
    id: 'anim-error-x',
    type: 'animation',
    name: 'Fehler X',
    tags: ['fehler', 'error', 'x', 'abbrechen'],
    category: 'alert',
    animationData: {
      v: '5.7.1',
      fr: 30,
      ip: 0,
      op: 30,
      w: 200,
      h: 200,
      nm: 'Error X',
      layers: []
    },
    duration: 1000,
    loop: false,
    autoplay: true,
  },
  {
    id: 'anim-warning',
    type: 'animation',
    name: 'Warnung',
    tags: ['warnung', 'warning', 'achtung', 'alert'],
    category: 'alert',
    animationData: {
      v: '5.7.1',
      fr: 30,
      ip: 0,
      op: 60,
      w: 200,
      h: 200,
      nm: 'Warning',
      layers: []
    },
    duration: 2000,
    loop: true,
    autoplay: true,
  },
  {
    id: 'anim-info',
    type: 'animation',
    name: 'Info',
    tags: ['info', 'information', 'hinweis', 'alert'],
    category: 'alert',
    animationData: createPulseAnimation('#3B82F6'),
    duration: 2000,
    loop: true,
    autoplay: true,
  },
];

// ===== EXPORT ALLE KATEGORIEN =====
export interface AnimationCategory {
  id: string;
  name: string;
  icon: string;
  description: string;
  animations: AnimationElement[];
}

export const ANIMATION_CATEGORIES: AnimationCategory[] = [
  {
    id: 'social',
    name: 'Social Icons',
    icon: '❤️',
    description: 'Herz, Teilen, Kommentar',
    animations: socialAnimations,
  },
  {
    id: 'loading',
    name: 'Loading',
    icon: '⏳',
    description: 'Spinner, Punkte, Fortschritt',
    animations: loadingAnimations,
  },
  {
    id: 'success',
    name: 'Success',
    icon: '✅',
    description: 'Haekchen, Konfetti, Daumen hoch',
    animations: successAnimations,
  },
  {
    id: 'transitions',
    name: 'Transitions',
    icon: '🔄',
    description: 'Fade, Slide, Bounce',
    animations: transitionAnimations,
  },
  {
    id: 'decorative',
    name: 'Dekorativ',
    icon: '✨',
    description: 'Sparkles, Sterne, Wellen',
    animations: decorativeAnimations,
  },
  {
    id: 'alert',
    name: 'Alerts',
    icon: '⚠️',
    description: 'Fehler, Warnung, Info',
    animations: alertAnimations,
  },
];

// ===== HELPER FUNCTIONS =====

/**
 * Gibt alle Animationen als flaches Array zurueck
 */
export function getAllAnimations(): AnimationElement[] {
  return ANIMATION_CATEGORIES.flatMap(cat => cat.animations);
}

/**
 * Sucht Animationen nach Name oder Tags
 */
export function searchAnimations(query: string): AnimationElement[] {
  const q = query.toLowerCase();
  return getAllAnimations().filter(anim =>
    anim.name.toLowerCase().includes(q) ||
    anim.tags.some(tag => tag.toLowerCase().includes(q)) ||
    (anim.category && anim.category.toLowerCase().includes(q))
  );
}

/**
 * Findet eine Animation anhand ihrer ID
 */
export function getAnimationById(id: string): AnimationElement | undefined {
  return getAllAnimations().find(anim => anim.id === id);
}

/**
 * Filtert Animationen nach Loop-Eigenschaft
 */
export function getLoopingAnimations(): AnimationElement[] {
  return getAllAnimations().filter(anim => anim.loop);
}

/**
 * Filtert Animationen nach Kategorie
 */
export function getAnimationsByCategory(categoryId: string): AnimationElement[] {
  const category = ANIMATION_CATEGORIES.find(cat => cat.id === categoryId);
  return category ? category.animations : [];
}

/**
 * Gibt die Anzahl aller Animationen zurueck
 */
export function getAnimationCount(): number {
  return getAllAnimations().length;
}

/**
 * Exportiert die Hex-zu-RGBA Konvertierung fuer externe Nutzung
 */
export { hexToRgba };
